import { useInfiniteQuery, keepPreviousData } from '@tanstack/react-query';
import { getEventAttendees } from '@api-client/api-client';
import { eventAttendeesKey } from '@lib/cache/events';
import { resolveNextPageParam, type PageParam } from '@lib/cache/questions';
import type { SearchKeyMeta } from '@lib/queryCacheOnError';

const ATTENDEES_PER_PAGE = 20;

interface UseEventAttendeesOptions {
  search?: string;
  enabled?: boolean;
}

/**
 * Going roster for the authed event page: the going-filtered attendee list, ordered
 * viewer, host, host-Space admins, then most-recent Going.
 * Infinite scroll over the reshaped GET /events/{id}/participants, same numeric-cursor
 * pagination as the feed. `search` threads a server-side name filter (debounced by the caller);
 * keepPreviousData holds the prior results while a new term loads so the list filters in place.
 */
export const useEventAttendees = (
  eventId: string,
  { search, enabled = true }: UseEventAttendeesOptions = {},
) => {
  const queryKey = eventAttendeesKey(eventId, search);
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetchNextPageError,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  } = useInfiniteQuery({
    queryKey,
    // The search is the key's last segment; keep it out of query-error logs.
    meta: { searchKeySegment: queryKey.length - 1 } satisfies SearchKeyMeta,
    queryFn: async ({ pageParam }) => {
      const params = {
        per_page: ATTENDEES_PER_PAGE,
        ...(search ? { search } : {}),
        ...(pageParam && 'cursor' in pageParam
          ? { cursor: pageParam.cursor }
          : pageParam && 'page' in pageParam
            ? { page: pageParam.page }
            : {}),
      };
      const response = await getEventAttendees(eventId, params);
      return response.data;
    },
    enabled: enabled && !!eventId,
    initialPageParam: null as PageParam | null,
    getNextPageParam: (lastPage, allPages) =>
      resolveNextPageParam(lastPage, allPages, { hookName: 'useEventAttendees' }),
    placeholderData: keepPreviousData,
  });

  // React Compiler memoizes this derivation; no useMemo needed.
  const seen = new Set<string>();
  const attendees = (data?.pages.flatMap(p => p.data) ?? []).filter(a =>
    seen.has(a.id) ? false : (seen.add(a.id), true),
  );

  return {
    attendees,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetchNextPageError,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  };
};

export { ATTENDEES_PER_PAGE };
